import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { compPlanStatementSelector } from "src/redux/selectors/custom-selector";
import {
  applyKpiSummarySheetFormatting,
  flattenExportColumns,
  getExportCellFormat,
  getExportSummaryData,
  getKpiSummaryFooterValue,
} from "./excelExportSectionOneUtils";

const ExcelExportCompPlanSectionOne = forwardRef((props, ref) => {
  const providerExportRef = useRef(null);
  const kpiExportRef = useRef(null);
  const { compPlanSummaryHeader, kpiPaymentSummary } = useSelector(compPlanStatementSelector, shallowEqual);

  const providerColumns = flattenExportColumns(compPlanSummaryHeader.columns);
  const kpiColumns = flattenExportColumns(kpiPaymentSummary.columns);
  const providerExportData = getExportSummaryData(providerColumns, compPlanSummaryHeader.data);

  const buildWorkbookOptions = () => {
    const sheets = [];

    if (providerExportRef.current && compPlanSummaryHeader.data?.length) {
      const providerSheet = providerExportRef.current.workbookOptions().sheets[0];
      providerSheet.name = "Provider Summary";
      sheets.push(providerSheet);
    }

    if (kpiExportRef.current && kpiPaymentSummary.data?.length) {
      const kpiSheet = kpiExportRef.current.workbookOptions().sheets[0];
      kpiSheet.name = "KPI Payment Summary";
      applyKpiSummarySheetFormatting(kpiSheet, kpiPaymentSummary.data, kpiColumns);
      sheets.push(kpiSheet);
    }

    return { sheets };
  };

  const saveWorkbook = (workbook, fileName = "Plan_Comp_Statement.xlsx") => {
    const saver = providerExportRef.current || kpiExportRef.current;
    saver?.save(workbook, fileName);
  };

  useImperativeHandle(
    ref,
    () => ({
      getWorkbookOptions: buildWorkbookOptions,
      saveWorkbook,
      excelExport: () => {
        const workbook = buildWorkbookOptions();
        if (workbook.sheets.length) {
          saveWorkbook(workbook);
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compPlanSummaryHeader, kpiPaymentSummary, providerColumns, kpiColumns]
  );

  return (
    <>
      {providerExportData?.length > 0 && (
        <ExcelExport
          ref={providerExportRef}
          data={providerExportData}
          fileName="Plan_Comp_Statement.xlsx"
        >
          {providerColumns.map((column) => (
            <ExcelExportColumn
              key={column.field}
              field={column.field}
              title={column.title}
              width={130}
              headerCellOptions={{ bold: true }}
              cellOptions={{ format: getExportCellFormat(column, compPlanSummaryHeader.data) }}
            />
          ))}
        </ExcelExport>
      )}
      {kpiPaymentSummary.data?.length > 0 && (
        <ExcelExport ref={kpiExportRef} data={kpiPaymentSummary.data} fileName="Plan_Comp_Statement.xlsx">
          {kpiColumns.map((column, index) => (
            <ExcelExportColumn
              key={column.field || index}
              field={column.field}
              title={column.title}
              width={130}
              headerCellOptions={{ bold: true, textAlign: "center" }}
              footerCellOptions={{ bold: true, textAlign: "center", background: "#7AC143" }}
              footer={() => (index === 0 ? "Total" : getKpiSummaryFooterValue(kpiPaymentSummary.data, column))}
              cellOptions={{
                textAlign: "center",
                format:
                  column.type === "number" && column.format
                    ? column.format.replace("{0:", "").replace("}", "")
                    : undefined,
              }}
            />
          ))}
        </ExcelExport>
      )}
    </>
  );
});

export default ExcelExportCompPlanSectionOne;
